import { assignAudienceGroupsToEntity } from 'Features/audience/stores/thunks/audience-group.thunks';
import { ActivityAudience } from 'Store/entities/activity/activity.type';
import { searchEntityPlayer } from 'Store/entities/entity-players/entity-players.thunk';

import {
  createEmailList,
  deleteAudienceGroup,
  updateEmailList,
  upsertMagicCode,
  upsertTargetingAudience,
} from './audience.api';

export type MagicCodeForm = { magicCode?: string };
export type EmailListForm = {
  emailList?: {
    addedEmails: string[];
    removedEmails: string[];
  };
};

export type CampaignAudienceForm = {
  campaign?: {
    targetedId: number;
  };
};

export type SegmentationsForm = { segmentations?: number[] };
export type AudienceFormValues = MagicCodeForm | EmailListForm | SegmentationsForm | CampaignAudienceForm | null;
export type AudienceFormType = {
  audiences: AudienceFormValues;
  audienceTypeChanged: boolean;
};

export const isAudienceMagicCode = (audience: AudienceFormValues): audience is MagicCodeForm =>
  !!(audience && 'magicCode' in audience);

export const isAudienceEmailList = (audience: AudienceFormValues): audience is EmailListForm =>
  !!(audience && 'emailList' in audience);

export const isAudienceSegmentation = (audience: AudienceFormValues): audience is SegmentationsForm =>
  !!(audience && 'segmentations' in audience);

export const isAudienceCampaign = (audience: AudienceFormValues): audience is CampaignAudienceForm =>
  !!(audience && 'campaign' in audience);

export const audienceFormatterFromStore = (audience?: ActivityAudience[]): AudienceFormValues => {
  if (!audience || audience.length === 0) return null;
  if (audience[0].type === 'magic-code') {
    return { magicCode: audience[0].segments?.[0]?.magicCodes?.[0] };
  }
  if (audience[0].type === 'email-list') {
    return { emailList: { addedEmails: [], removedEmails: [] } };
  }
  if (audience[0].type === 'segmentation' || audience[0].type === 'all') {
    return { segmentations: audience.map(({ groupId }) => groupId) };
  }
  if (audience[0].type === 'audience') {
    return { campaign: audience[0].targetedId ? { targetedId: audience[0].targetedId } : undefined };
  }
  return null;
};

type SaveAudienceParams = {
  hasAudienceChanged: boolean;
  audienceFormValues: AudienceFormValues;
  activityAudience?: ActivityAudience[];
  entity: {
    id: number;
    type: 'activity';
  };
  dispatch: any;
};

export const saveAudience = async ({
  hasAudienceChanged,
  audienceFormValues,
  activityAudience,
  entity,
  dispatch,
}: SaveAudienceParams) => {
  // the audience type is the same, we only need to update the values
  if (!hasAudienceChanged && activityAudience?.[0]) {
    if (isAudienceMagicCode(audienceFormValues) && audienceFormValues.magicCode) {
      await upsertMagicCode(audienceFormValues.magicCode, activityAudience[0].groupId);
      return 'updated';
    }

    if (isAudienceEmailList(audienceFormValues) && audienceFormValues.emailList) {
      await updateEmailList(audienceFormValues.emailList, activityAudience[0].groupId);
      dispatch(
        searchEntityPlayer({
          entity: { entityId: entity.id, entityType: entity.type },
          searchParams: { name: '', offset: 0 },
        }),
      );
      return 'updated';
    }

    if (isAudienceSegmentation(audienceFormValues) && audienceFormValues.segmentations) {
      const { unwrap } = dispatch(
        assignAudienceGroupsToEntity({
          entityType: entity.type,
          entityId: entity.id,
          audienceGroupIds: audienceFormValues.segmentations,
        }),
      );
      await unwrap();
      return 'updated';
    }

    if (isAudienceCampaign(audienceFormValues) && audienceFormValues.campaign?.targetedId) {
      await upsertTargetingAudience(entity.id, audienceFormValues.campaign.targetedId);
      dispatch(
        searchEntityPlayer({
          entity: { entityId: entity.id, entityType: entity.type },
          searchParams: { name: '', offset: 0 },
        }),
      );

      return 'updated';
    }
  }
  // if the audience has changed we need to create and assign audience groups
  if (hasAudienceChanged || !activityAudience?.[0]) {
    if (isAudienceMagicCode(audienceFormValues) && audienceFormValues.magicCode) {
      const response = await upsertMagicCode(audienceFormValues.magicCode);
      const { unwrap } = dispatch(
        assignAudienceGroupsToEntity({
          entityType: entity.type,
          entityId: entity.id,
          audienceGroupIds: [response.audienceGroupId],
        }),
      );
      await unwrap();
    }
    if (isAudienceEmailList(audienceFormValues) && audienceFormValues.emailList) {
      const response = await createEmailList(audienceFormValues.emailList.addedEmails);
      const { unwrap } = dispatch(
        assignAudienceGroupsToEntity({
          entityType: entity.type,
          entityId: entity.id,
          audienceGroupIds: [response.audienceGroupId],
        }),
      );
      await unwrap();
    }
    if (isAudienceCampaign(audienceFormValues) && audienceFormValues.campaign?.targetedId) {
      await upsertTargetingAudience(entity.id, audienceFormValues.campaign.targetedId);
    }
    if (isAudienceSegmentation(audienceFormValues) && audienceFormValues.segmentations) {
      const { unwrap } = dispatch(
        assignAudienceGroupsToEntity({
          entityType: entity.type,
          entityId: entity.id,
          audienceGroupIds: audienceFormValues.segmentations,
        }),
      );

      await unwrap();
    }

    // delete unused audience group if it's magic code or email list
    if (
      activityAudience?.[0] &&
      (activityAudience[0].type === 'email-list' || activityAudience[0].type === 'magic-code')
    ) {
      await deleteAudienceGroup(activityAudience[0].groupId);

      dispatch(
        searchEntityPlayer({
          entity: { entityId: entity.id, entityType: entity.type },
          searchParams: { name: '', offset: 0 },
        }),
      );
    }
    return 'created & assigned + deleted';
  }
};
